import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchBscOutput,
  clearBscOutput,
  reopenBscPractice,
} from "../../api/client";
import { useAuth } from "../../auth/useAuth";
import { canManageQueue } from "../../auth/permissions";
import type { BscOutputItem, BscOutputListResponse, BscHanjaReadiness } from "../../api/types";
import { HanjaBadges } from "../../lib/hanja";
import { useToast } from "../../context/ToastContext";
import { ConfirmModal } from "../../components/ConfirmModal";
import { BscPdfAccordion, getPdfsForDoc, getAllPdfFilenames } from "../../components/BscPdfAccordion";

export function BscQueue() {
  return (
    <div>
      <h1 className="page-title" style={{ marginBottom: "var(--space-4)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 24, marginRight: 8, verticalAlign: "middle" }}>outbox</span>
        Coda Output BSC
      </h1>
      <QueueTab />
    </div>
  );
}

function QueueTab() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { user } = useAuth();
  const canWrite = canManageQueue(user?.roles ?? []);
  const [data, setData] = useState<BscOutputListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(() => {
    setIsLoading(true);
    fetchBscOutput(1, 100)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleClearQueue = async () => {
    setActionLoading(true);
    try {
      const res = await clearBscOutput();
      const n = res.reopened?.length ?? 0;
      const errs = res.errors?.length ?? 0;
      addToast(
        errs > 0 ? "error" : "success",
        errs > 0
          ? `Coda svuotata con ${errs} errori — ${n} riaperte`
          : `Coda svuotata — ${n} pratiche riaperte`,
      );
      setConfirmClear(false);
      load();
    } catch (e: unknown) {
      addToast("error", `Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopen = async (id: string) => {
    setActionLoading(true);
    try {
      const res = await reopenBscPractice(id);
      const purge = res.output_purge;
      const errs = purge?.errors?.length ?? 0;
      addToast(
        errs > 0 ? "error" : "success",
        errs > 0
          ? `Riaperta con ${errs} errori`
          : `Pratica riaperta. JSON rimosso (${purge.blobs_deleted} file).`,
      );
      setConfirmDeleteId(null);
      load();
    } catch (e: unknown) {
      addToast("error", `Errore: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadJson = (item: BscOutputItem) => {
    const blob = new Blob([JSON.stringify(item.final_data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bsc_${item.bl_number || item.id.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const items = data?.items ?? [];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-3)", flexWrap: "wrap", rowGap: "var(--space-2)" }}>
        <span className="text-muted" style={{ fontSize: "var(--font-size-sm)", minWidth: 0 }}>
          {data ? `${data.total} pratiche in coda` : "Caricamento..."}
        </span>
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          {items.length > 0 && canWrite && (
            <button className="btn btn-danger btn-sm" onClick={() => setConfirmClear(true)} disabled={actionLoading}>
              <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete_sweep</span>
              Svuota coda
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={isLoading}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>refresh</span>
            Aggiorna
          </button>
        </div>
      </div>

      {isLoading && !data && <div className="loading-center"><div className="spinner spinner-lg" /></div>}

      {error && (
        <div className="alert alert-danger">
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span>
          Errore: {error}
        </div>
      )}

      {data && items.length === 0 && !isLoading && (
        <div className="card text-center text-muted" style={{ padding: "var(--space-10)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 48, display: "block", marginBottom: "var(--space-2)", opacity: 0.4 }}>inbox</span>
          Coda vuota &mdash; nessuna pratica BSC approvata
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", opacity: isLoading && data ? 0.6 : 1 }}>
        {items.map((item) => {
          const isExpanded = expandedId === item.id;
          return (
            <div key={item.id} className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "var(--space-3)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", marginBottom: "var(--space-1)" }}>
                  <span style={{ fontFamily: "var(--font-family-mono)", fontSize: "var(--font-size-xs)",
                    background: "var(--indaco-gray-100)", color: "var(--indaco-gray-600)",
                    padding: "1px 6px", borderRadius: 4, flexShrink: 0 }}>
                    {item.id.slice(0, 6)}
                  </span>
                  <span style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--font-size-base)", flex: 1 }}>
                    {item.bl_number || "BL sconosciuto"}
                  </span>
                  {item.has_bl && <span className="badge badge-info">BL</span>}
                  {item.has_invoice && <span className="badge badge-info">Invoice</span>}
                  {item.invoice_total != null && (
                    <span className="badge badge-success">
                      {item.invoice_total.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                    </span>
                  )}
                  <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px", flexShrink: 0 }}
                    title="Apri pratica"
                    onClick={() => navigate(`/bsc/${item.id}`)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 15, marginRight: 4 }}>open_in_new</span>
                    Pratica
                  </button>
                </div>

                <div style={{
                  display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px var(--space-3)",
                  fontSize: "var(--font-size-sm)", marginBottom: "var(--space-2)", marginTop: "var(--space-2)",
                }}>
                  {item.vessel_name && (
                    <>
                      <span className="text-muted">Nave</span>
                      <span>{item.vessel_name}</span>
                    </>
                  )}
                  {(item.pol || item.pod) && (
                    <>
                      <span className="text-muted">Rotta</span>
                      <span>{item.pol || "?"} → {item.pod || "?"}{item.pod_country ? ` (${item.pod_country})` : ""}</span>
                    </>
                  )}
                  {item.importer_name && (
                    <>
                      <span className="text-muted">Importatore</span>
                      <span>
                        {item.importer_name}
                        {item.importer_country && (
                          <span className="badge badge-neutral" style={{ marginLeft: 6, fontSize: "var(--font-size-xs)" }}>
                            {item.importer_country}
                          </span>
                        )}
                      </span>
                    </>
                  )}
                  {item.exporter_name && (
                    <>
                      <span className="text-muted">Esportatore</span>
                      <span>{item.exporter_name}</span>
                    </>
                  )}
                  {item.invoice_number && (
                    <>
                      <span className="text-muted">Fattura</span>
                      <span>
                        {item.invoice_number}
                        {item.invoice_total != null && (
                          <span className="text-muted" style={{ marginLeft: 6 }}>
                            {item.invoice_total.toLocaleString("it-IT", { minimumFractionDigits: 2 })} {item.invoice_currency || ""}
                          </span>
                        )}
                      </span>
                    </>
                  )}
                  <span className="text-muted">Campi estratti</span>
                  <span>{item.non_null_count}/26</span>
                  <span className="text-muted">HANJA</span>
                  <span><HanjaBadges hanja={item.llm_metadata?.hanja as BscHanjaReadiness | undefined} /></span>
                  {/* Extra CIF (Incoterm/Nolo/Assicurazione) — opzionali, NON
                      conteggiati in "Campi estratti"/26 sopra. Questi campi sono
                      uso interno DB (mai nel JSON di output verso Frabemar),
                      quindi si leggono da bsc_data, non da final_data/output_data. */}
                  <span className="text-muted" title="Incoterm, Nolo, Assicurazione — opzionali, non contano nel conteggio 26">Extra CIF</span>
                  <span>
                    {["terms_of_delivery", "freight", "insurance"].filter(
                      (k) => (item.bsc_data as Record<string, unknown> | undefined)?.[k] != null
                        && (item.bsc_data as Record<string, unknown> | undefined)?.[k] !== "",
                    ).length}/3
                  </span>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-2)" }}>
                  <span style={{ fontFamily: "var(--font-family-mono)", fontSize: "var(--font-size-xs)", color: "var(--indaco-gray-400)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}
                    title={item.output_path || ""}>
                    {item.output_path}
                  </span>
                  {item.approved_at && (
                    <span className="text-muted" style={{ fontSize: "var(--font-size-xs)", whiteSpace: "nowrap" }}>
                      Approvata il {formatDate(item.approved_at)}
                    </span>
                  )}
                </div>

                <div style={{ display: "flex", gap: "var(--space-2)", borderTop: "1px solid var(--indaco-gray-100)", paddingTop: "var(--space-2)" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setExpandedId(isExpanded ? null : item.id)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{isExpanded ? "visibility_off" : "data_object"}</span>
                    {isExpanded ? "Nascondi JSON" : "Vedi JSON"}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => handleDownloadJson(item)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
                    Scarica JSON
                  </button>
                  {canWrite && (
                    <button className="btn btn-danger btn-sm" style={{ marginLeft: "auto" }} onClick={() => setConfirmDeleteId(item.id)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>undo</span>
                      Riapri
                    </button>
                  )}
                </div>
              </div>

              {/* Anteprima PDF originali (BL + Invoice) — lazy load su espansione */}
              {(() => {
                const allPdfs = getAllPdfFilenames(item.pdf_files);
                const blPdfs = getPdfsForDoc(item.pdf_files ?? null, "bl");
                const invPdfs = getPdfsForDoc(item.pdf_files ?? null, "invoice");
                if (allPdfs.length === 0) return null;
                return (
                  <div style={{ padding: "0 var(--space-3) var(--space-3)", display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                    <BscPdfAccordion
                      practiceId={item.id}
                      title={`Bill of Lading${blPdfs.length ? ` (${blPdfs.length})` : ""}`}
                      icon="directions_boat"
                      headerColor="var(--indaco-brand-primary)"
                      headerBg="var(--indaco-brand-primary-light)"
                      pdfFilenames={blPdfs}
                      emptyLabel="Nessun BL in questa pratica"
                    />
                    <BscPdfAccordion
                      practiceId={item.id}
                      title={`Fattura commerciale${invPdfs.length ? ` (${invPdfs.length})` : ""}`}
                      icon="receipt_long"
                      headerColor="var(--indaco-success)"
                      headerBg="var(--indaco-success-light)"
                      pdfFilenames={invPdfs}
                      emptyLabel="Nessuna fattura in questa pratica"
                    />
                  </div>
                );
              })()}

              {isExpanded && (
                <pre style={{
                  margin: 0, padding: "var(--space-3)",
                  background: "var(--indaco-gray-50)", borderTop: "1px solid var(--indaco-gray-200)",
                  fontSize: "var(--font-size-xs)", overflow: "auto", maxHeight: 400,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                }}>
                  {JSON.stringify(item.final_data, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>

      {confirmClear && (
        <ConfirmModal
          title="Svuota coda output BSC"
          message={`Vuoi davvero svuotare la coda? Tutte le ${items.length} pratiche approvate verranno riaperte (status → pending_review) e i JSON rimossi dal filesystem.`}
          confirmLabel="Svuota"
          danger
          onConfirm={handleClearQueue}
          onCancel={() => setConfirmClear(false)}
        />
      )}
      {confirmDeleteId && (
        <ConfirmModal
          title="Riapri pratica BSC"
          message="Vuoi davvero riaprire questa pratica? Il JSON di output verrà cancellato dal filesystem e la pratica tornerà in stato pending_review."
          confirmLabel="Riapri"
          danger
          onConfirm={() => handleReopen(confirmDeleteId)}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

